// Practice-wide "Instant video calls" switch for the CRM header. While it is on and
// this tab is visible, this tab also tells the backend it is present (every 30 s):
// a ringing request only goes out when someone is, and rings everyone who is.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Switch, Tooltip, Typography } from '@mui/material';
import { useSelector } from '../../redux/store';
import Iconify from '../Iconify';
import { getInstantVideoState, sendInstantVideoHeartbeat, setInstantVideoEnabled } from './instantVideoApi';

export const HEARTBEAT_MS = 30000;

export default function InstantVideoSwitch() {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    // null = unknown/unavailable (switch hidden); otherwise the practice's state.
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    // A 403 on PUT: this user can look but not change it.
    const [readOnly, setReadOnly] = useState(false);
    const practiceRef = useRef(practiceId);
    practiceRef.current = practiceId;

    useEffect(() => {
        let active = true;
        setState(null); setFailed(false); setReadOnly(false);
        if (!practiceId) return undefined;
        getInstantVideoState(practiceId).then((result) => { if (active && result.ok) setState(result); }).catch(() => {});
        return () => { active = false; };
    }, [practiceId]);

    const enabled = !!state?.enabled;
    useEffect(() => {
        if (!enabled || !practiceId) return undefined;
        const beat = () => {
            if (document.visibilityState === 'hidden') return;
            // The reply carries the live status (opening hours, who is online); a 403 is ignored.
            sendInstantVideoHeartbeat(practiceId).then((result) => {
                if (result.ok && practiceRef.current === practiceId) setState((old) => (old ? { ...old, ...result, ok: undefined } : old));
            }).catch(() => {});
        };
        beat();
        const timer = setInterval(beat, HEARTBEAT_MS);
        // Coming back to the tab counts as present straight away.
        document.addEventListener('visibilitychange', beat);
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', beat); };
    }, [enabled, practiceId]);

    const toggle = useCallback(async (event) => {
        const next = event.target.checked;
        const target = practiceId;
        setBusy(true); setFailed(false);
        let result;
        try { result = await setInstantVideoEnabled(target, next); } catch { result = { ok: false }; }
        if (practiceRef.current !== target) return;
        setBusy(false);
        if (result.ok) setState((old) => ({ ...old, ...result, ok: undefined }));
        else if (result.forbidden) setReadOnly(true);
        else {
            setFailed(true);
            // A refusal (e.g. no longer offered) means our picture is stale: re-read it.
            getInstantVideoState(target).then((fresh) => { if (fresh.ok && practiceRef.current === target) setState(fresh); }).catch(() => {});
        }
    }, [practiceId]);

    // Offered = PMS on + a published video event with the option on. An already-on switch
    // stays visible so staff can always turn it off.
    if (!state || (!state.offered && !state.enabled)) return null;
    // Closed: the backend switches it straight back off, so say why and when it can go on.
    const closed = state.inHours === false;
    let hint = '';
    if (closed) hint = state.opensWhen ? `Closed · opens ${state.opensWhen}` : 'Closed now';
    else if (enabled) {
        if (state.available) hint = 'Available';
        else if (state.staffOnline && state.capacity === false) hint = 'No free diary right now';
        else hint = 'No one online';
    }
    let title = enabled
        ? 'On: a patient asking to talk now rings everyone with the CRM open for this practice. The first to accept takes the call.'
        : 'Off: patients are not offered an instant video call.';
    if (hint) title = `${hint}. ${title}`;
    if (closed && !enabled) title = state.opensWhen ? `Closed now. You can turn this on from ${state.opensWhen}.` : 'Closed now. You can turn this on when the practice opens.';
    if (readOnly) title = 'Ask a manager to change this.';
    if (failed) title = 'Could not change this just now. Try again.';
    return (
        <Tooltip title={title}>
            <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 0.5, borderRadius: 1, cursor: readOnly ? 'default' : 'pointer', color: enabled ? 'success.main' : 'text.secondary' }}>
                <Iconify icon="eva:video-fill" width={20} />
                <Box sx={{ display: { xs: 'none', md: 'block' }, lineHeight: 1.1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: failed ? 'error.main' : 'inherit' }}>Instant video</Typography>
                    {hint && <Typography variant="caption" color={state.available ? 'success.main' : 'text.secondary'} sx={{ display: 'block', fontSize: 10 }}>{hint}</Typography>}
                </Box>
                <Switch size="small" color="success" checked={enabled} disabled={busy || readOnly || (closed && !enabled)} onChange={toggle} inputProps={{ 'aria-label': 'Instant video calls' }} />
                {busy && <CircularProgress size={14} aria-label="Saving" />}
            </Box>
        </Tooltip>
    );
}
